import type { WorkerAuthContext, WorkerMessageMap } from '~/composables/useServiceWorker'
import { useAssetCache } from '~/composables/map/useAssetCache'
import type { MapFeaturesResponse } from '~/types/map-assets.types'
import { parseMapFeaturesArray } from '~/utils/map/map-features-parser.utils'

// Fetch assets from API in worker context
export const fetchAssetsInWorker = async (
  originHash: string,
  excludeOrigins: string[],
  authContext: WorkerAuthContext
): Promise<MapFeaturesResponse> => {
  const url = new URL(`${authContext.apiUrl}/assets/map-features`)
  url.searchParams.set('origin', originHash)

  if (excludeOrigins.length > 0) {
    for (const hash of excludeOrigins) {
      url.searchParams.append('exclude', hash)
    }
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    authorization: `Bearer ${authContext.accessToken}`
  }

  if (authContext.licenseId !== null) {
    headers['X-Fundamentum-Licence-Id'] = String(authContext.licenseId)
  }

  if (authContext.siteId !== null) {
    headers['X-Site-Id'] = String(authContext.siteId)
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers
  })

  if (!response.ok) {
    const isAuthError = response.status === 401 || response.status === 403
    throw new WorkerFetchError(`API request failed: ${response.status} ${response.statusText}`, isAuthError)
  }

  const data: MapFeaturesResponse = await response.json()
  return data
}

// Custom error class to indicate retryable errors
class WorkerFetchError extends Error {
  constructor(
    message: string,
    public retryable: boolean
  ) {
    super(message)
    this.name = 'WorkerFetchError'
  }
}

// Send message back to main thread
const postMessage = (type: string, payload: unknown): void => {
  self.postMessage({ type, payload })
}

// Handle progressive loading request from main thread
export const handleLoadAssetsProgressively = async ({
  requestId,
  originHash,
  precision: _precision, // Preserved for future use
  excludeOrigins,
  authContext
}: WorkerMessageMap['loadAssetsProgressively']): Promise<void> => {
  const cache = useAssetCache()

  let cachedCount = 0

  // Step 1: Read from IDB cache and stream immediately (non-blocking).
  // Skipped when a site filter is active: the local cache is licence-scoped
  // only (not site-scoped), so cached entries could belong to a different
  // site than the one currently selected and would incorrectly show up
  // alongside the server's site-filtered response.
  if (authContext.siteId === null) {
    try {
      const hashesToQuery = originHash ? [originHash] : await cache.getCachedHashes()
      const cachedAssets = await cache.getCachedAssets(hashesToQuery)

      if (cachedAssets.length > 0) {
        cachedCount = cachedAssets.length
        postMessage('assetsCacheChunk', {
          requestId,
          originHash,
          assets: cachedAssets,
          source: 'cache'
        } satisfies WorkerMessageMap['assetsCacheChunk'])
      }
    } catch (error) {
      console.warn('[Service Worker] Cache read failed, proceeding with server fetch:', error)
    }
  }

  // Step 2: Fetch from server
  try {
    const serverResponse = await fetchAssetsInWorker(originHash, excludeOrigins, authContext)
    const parsedAssets = parseMapFeaturesArray(serverResponse.assets, serverResponse.headers)

    if (parsedAssets.length > 0) {
      // Stream server data to main thread
      postMessage('assetsServerChunk', {
        requestId,
        originHash,
        assets: parsedAssets,
        source: 'server'
      } satisfies WorkerMessageMap['assetsServerChunk'])

      // Step 3: Persist to IDB and await before signalling completion (filtering reads IDs back from IDB)
      try {
        await Promise.all([
          cache.cacheAssets(parsedAssets),
          cache.updateLastFetchTime(),
          originHash ? cache.addCachedHashes([originHash]) : Promise.resolve(),
          authContext.licenseId !== null ? cache.setCurrentLicenseId(authContext.licenseId) : Promise.resolve()
        ])
      } catch (cacheError) {
        console.error('[Service Worker] Error caching assets:', cacheError)
      }
    }

    // Step 4: Signal completion
    postMessage('loadAssetsStepComplete', {
      requestId,
      originHash,
      cachedCount,
      serverCount: parsedAssets.length
    } satisfies WorkerMessageMap['loadAssetsStepComplete'])
  } catch (error) {
    const isRetryable = error instanceof WorkerFetchError ? error.retryable : false
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'

    postMessage('loadAssetsError', {
      requestId,
      error: errorMessage,
      retryable: isRetryable
    } satisfies WorkerMessageMap['loadAssetsError'])
  }
}

// Handle ping for health check
export const handlePing = (): void => {
  postMessage('pong', {})
}

// Original handler for caching fetched assets (used by main-thread fallback)
export const handleFetchedMapAssets = async ({
  assets,
  hashes,
  licenseId
}: WorkerMessageMap['mapAssetsFetched']): Promise<void> => {
  const cache = useAssetCache()

  const promises = [cache.cacheAssets(assets), cache.updateLastFetchTime(), cache.addCachedHashes(hashes)]

  if (licenseId !== null) {
    promises.push(cache.setCurrentLicenseId(licenseId))
  }

  await Promise.all(promises)
}
